import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {environment} from '@env/environment';
import {ApplicationEventModel, ApplicationModel, CSPostResponseModel, CustomerConsentModel, SearchModel} from '@service/models';
import {AuthenticationService} from '@core/auth/authentication.service';
import {ApprovalApplicationModel} from '@service/models/approvalApplication.model';
import {ExtendedAttributesModel} from '@service/models/extendedAttributes.model';
import {LogRocketService} from '@core/util-services/logrocket.service';

const API_APPLICATION_URL = environment.apiUrl + '/application';
const API_BASIC_ACCOUNT_URL = environment.depositApiUrl;
const API_BASIC_ACCOUNT_ENTRYPOINT_URL = environment.depositApiUrlWithoutToken;
const API_ENTRYPOINT_URL = environment.apiUrlWithoutToken;

@Injectable({
    providedIn: 'root'
})
export class ApplicationService {

    public model: any = {grant_type: '', username: '', password: ''};
    public approvalApplicationData: ApprovalApplicationModel;

    constructor(
        private authenticationService: AuthenticationService,
        private http: HttpClient,
        private logRocketService: LogRocketService) {
    }

    // Create customer app or short app requires only Basic Auth
    // After application success, create authentication
    createApplication(app: ApplicationModel, ioBlackBoxValue: any = ''): Observable<CSPostResponseModel> {
        const createAppUrl = app.productType === 'DEPOSIT' ? API_BASIC_ACCOUNT_ENTRYPOINT_URL + '/applications' : environment.apiUrlWithoutToken + '/create-app/v1';
        return this.http.post<CSPostResponseModel>(createAppUrl,
            app, {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}}).pipe(tap(res => this.logRocketService.identifyApplication(res?.applicationId)));
    }

    // CREATE =>  POST: add a new short application to the server
    createShortApplication(app: ApplicationModel): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(API_ENTRYPOINT_URL + '/short-app',
            app, {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}}).pipe(tap(res => this.logRocketService.identifyApplication(res?.applicationId)));
    }

    addConsent(applicationId: number, applicantId: number, consent: CustomerConsentModel): Observable<CSPostResponseModel> {
        return this.http.put<CSPostResponseModel>(environment.apiUrl + `/consent/application/${applicationId}/applicant/${applicantId}`, consent);
    }

    addExtendedAttributes(applicationId: number, extendedAttributes: ExtendedAttributesModel[]): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `/application/extendedattributes/${applicationId}`, extendedAttributes);
    }

    addConsents(applicationId: number, applicantId: number, consents: CustomerConsentModel[]): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `/consent/application/${applicationId}/applicant/${applicantId}`, consents);
    }

    addApplicationEvent(applicationId: number, notes: string): Observable<CSPostResponseModel> {
        const event = new ApplicationEventModel();
        event.applicationId = applicationId;
        event.notes = notes;
        return this.http.put<CSPostResponseModel>(environment.apiUrl + `/application/event`, event);
    }

    findApplication(_appId: number): Observable<ApplicationModel> {
        return this.http.get<ApplicationModel>(API_APPLICATION_URL + `/${_appId}`)
            .pipe(tap(res => this.logRocketService.identifyApplication(res?.applicationId)));
    }

    updateApplicationStatus(_appId: number, body: any): Observable<any> {
        return this.http.put<any>(API_APPLICATION_URL + `/${_appId}/status`, body);
    }

    searchApplication(search: SearchModel, isCoBoSearch = false, memberSearch = false): Observable<CSPostResponseModel> {
        let url = '/search';
        if (isCoBoSearch) {
            url = '/search/coapplicant';
        }
        if (memberSearch) {
            const additionalFeatures = [];
            /* Format API REQUEST data if not available*/
            if (search && search.selectedProduct && search.selectedProduct.product && !search.additionalDetails) {
                search.additionalDetails = search.selectedProduct;
                search.additionalDetails.additionalFeatures.forEach(item => {
                    if (item.value) {
                        additionalFeatures.push(item.value);
                    }
                });
                search.additionalDetails.additionalFeatures = additionalFeatures;
                search.additionalDetails.product = search.selectedProduct.product.value;
                search.additionalDetails.subProduct = search.selectedProduct.subProduct.value;
                delete search.selectedProduct;
            }
            url = '/member/search';
        }
        search.productType = 'deposit';
        return this.http.post<CSPostResponseModel>(environment.apiUrlWithoutToken + url, search,
            {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}});
    }

    bundleService(applicationId: number): Observable<any> {
        const multipleAPIs = [];
        multipleAPIs.push({url: environment.apiUrl + `/application/${applicationId}`});
        multipleAPIs.push({url: API_BASIC_ACCOUNT_URL + `/applications/${applicationId}/products`});
        // multipleAPIs.push({url: environment.apiUrl + `/collateral/${applicationId}`});

        return forkJoin(
            multipleAPIs.length
                ? multipleAPIs.map(model =>
                    this.http.get(`${model.url}`).pipe(
                        map(response => ({value: response})),
                        catchError(e => of({error: e.error}).pipe(map(err => err))),
                    ),
                )
                : of({error: {code: '4005', message: 'error'}}).pipe(map(err => err)),
        ).pipe(
            map((res: any) => {
                return {responses: res, error: (res[0] || {}).error};
            }),
        );
    }

    uploadAppDocument(data, _appId: number, coApplicantConsentFlow = false): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'multipart/form-data');

        let url = environment.apiUrl + `/documents/${environment.institutionId}/${_appId}/upload`;
        if (coApplicantConsentFlow) {
            url = environment.apiUrl + `/documents/coapplicant/${environment.institutionId}/${_appId}/upload`;
        }
        return this.http.post<any>(url, data, {headers});
    }

    getRequestedDocuments(_appId: number): any {
        return this.http.get<CSPostResponseModel>(environment.apiUrl + `/documents/requested/application/${_appId}`);
    }

    getExistingMemberDetails(memberData: any): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.apiUrlWithoutToken + '/member/search', memberData,
            {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}});
    }

    searchCompanyByName(s: string): Observable<any> {
        const url = 'https://7MV7A9XWSE.algolia.net/1/indexes/SEG/query';
		const body = {
			query: `${s}`,
			filters: 'institutionlookup:\'' + environment.institutionId + '_Organization\'',
			attributesToRetrieve: [
				"code",
				"value"
			],
            restrictSearchableAttributes:["value"]
		}

		return this.http.post(url, body, {
			headers: {
				'X-Skip-Headers': environment.auth.interceptorSkipHeader,
				'X-Algolia-Api-Key': environment.ALGOLIA_API_KEY,
				'X-Algolia-Application-Id': environment.ALGOLIA_APPLICATION_ID
			}
		});
    }

    checkPreEligibilty(appId: number): Observable<CSPostResponseModel> {
        const url = `${environment.apiUrl}/application/preeligibilty/${appId}`;
        return this.http.put<CSPostResponseModel>(url, {});
    }

    postHtmlToPdf(_appId: number, data: any, isCrFlow: boolean = false): any {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `${isCrFlow ? '/coapplicant' : ''}/documents/html/to/pdf/application/${_appId}`, data);
    }

    postHtmlToPdfCoApplicant(_appId: number, data: any): any {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `/coapplicant/documents/html/to/pdf/application/${_appId}`, data);
    }

    putFraud(appId: number, coBoFlow = false) {
        let headers = new HttpHeaders();
        if (coBoFlow) {
            headers = headers.append('flow', 'COAPPPLICANT');
        }
        return this.http.put<CSPostResponseModel>(`${API_BASIC_ACCOUNT_URL}/applications/${appId}/fraud`, {}, {headers});
    }

    addCoApplicantConsent(applicationId: number, consent: CustomerConsentModel[]): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `/coapplicant/consent/${applicationId}`, consent);
    }

    /**
     * Get List of product for deposits
     */
    getProductList() {
        return this.http.get<CSPostResponseModel>(environment.apiUrlWithoutToken + '/deposit/account/DEPOSIT/products',
            {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}});
    }

    /**
     * Get List of product for business deposits
     */
    getBusinessProductList() {
        return this.http.get<CSPostResponseModel>(environment.apiUrlWithoutToken + '/deposit/account/BUSINESS_DEPOSIT/products',
            {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}});
    }

    /**
     * Service is to get list of stipulation
     * @param _applicationId
     */
    getStipulations(_applicationId: number, coApplicantFlow = false) {
        let url = environment.apiUrl + `/stips/${_applicationId}`;
        if (coApplicantFlow) {
            url = environment.apiUrl + `/stips/coapplicant/${_applicationId}`;
        }
        return this.http.get<any>(url);
    }

    /**
     * Get list of selected products
     * @param _applicationId
     */
    getSelectedProducts(_applicationId: number) {
        return this.http.get<CSPostResponseModel>(environment.apiUrl + '/deposit/account/applications/' + _applicationId + '/products');
    }

    validateZipCode(s: string): Observable<any> {
		const url = 'https://7MV7A9XWSE.algolia.net/1/indexes/ZIP/query';
		const body = {
			query: `${s}`,
			filters: `ZIP:'${s}'`,
			attributesToRetrieve: [
				"ZIP",
				"City",
				"ST"
			]
		}

		return this.http.post(url, body, {
			headers: {
				'X-Skip-Headers': environment.auth.interceptorSkipHeader,
				'X-Algolia-Api-Key': environment.ALGOLIA_API_KEY,
				'X-Algolia-Application-Id': environment.ALGOLIA_APPLICATION_ID
			}
		});
	}

    validateRoutingNumber(rn: string) {
        const url = 'https://7MV7A9XWSE.algolia.net/1/indexes/routingNumber/query';
		const body = {
			query: `${rn}`,
			filters: "institutionlookup:routingNumber",
			attributesToRetrieve: [
				"code",
				"value"
			]
		}

		return this.http.post(url, body, {
			headers: {
				'X-Skip-Headers': environment.auth.interceptorSkipHeader,
				'X-Algolia-Api-Key': environment.ALGOLIA_API_KEY,
				'X-Algolia-Application-Id': environment.ALGOLIA_APPLICATION_ID
			}
		});
    }

    unsubscribe(code: any, reasons: string): Observable<any> {
        return this.http.put(environment.apiUrl + `/unsubscribe/${environment.institutionId}?code=${code}`, {reason: reasons},
            {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}});
    }

    getPartnerDetails(merchantId: string): Observable<any> {
        const url = `${environment.apiUrl}/partnerdetails/partner-details/${environment.institutionId}/web/${merchantId}`;
        const headers = new HttpHeaders({
            'X-Skip-Interceptor': environment.auth.interceptorSkipHeader
        });

        return this.http.get<ApplicationModel>(url, { headers });
    }


}
